import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";
import { useConfirmModal } from "../../../core/hooks/useConfirmModal";

interface UnsavedChangesContextType {
  setIsDirty: (dirty: boolean) => void;
  requestNavigation: (action: () => void) => void;
  bypassGuard: (action: () => void) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextType>({
  setIsDirty: () => {},
  requestNavigation: (action) => action(),
  bypassGuard: (action) => action(),
});

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [isDirty, setIsDirty] = useState(false);
  const skipGuardRef = useRef(false);
  const { showModal, ConfirmModalComponent } = useConfirmModal();

  const runWithoutGuard = useCallback((action: () => void) => {
    skipGuardRef.current = true;
    action();
    queueMicrotask(() => {
      skipGuardRef.current = false;
    });
  }, []);

  const requestNavigation = useCallback(
    (action: () => void) => {
      if (isDirty && !skipGuardRef.current) {
        showModal(t("modal.unsavedTitle"), t("modal.unsavedMessage"), () =>
          runWithoutGuard(action)
        );
        return;
      }
      action();
    },
    [isDirty, showModal, t, runWithoutGuard]
  );

  const bypassGuard = runWithoutGuard;

  return (
    <UnsavedChangesContext.Provider
      value={{ setIsDirty, requestNavigation, bypassGuard }}
    >
      {children}
      <ConfirmModalComponent />
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChanges() {
  return useContext(UnsavedChangesContext);
}
